import React, { useState, useEffect } from 'react';
import { Container, StyledLabelP } from 'modules/Sty';
import UserInvoiceList from './UserInvoiceList';
import InvoiceFilterOptions from './InvoiceFilterOptions';
import { getUserInvoices_api, UserInvoice } from 'api/userApi';
import useUser, { UserExistsState } from 'Hooks/useUser';
import ButtonSymphony from 'elements/ButtonSymphony';
import dayjs from 'dayjs';

interface InvoiceFilter {
    brands: string[];
    statuses: string[];
    dateModifier: 'all_time' | 'in_between' | 'before' | 'after' | 'last_days';
    startDate: string;
    endDate: string;
    filterDays: string;
    amountModifier: 'no_amount_filter' | 'equal' | 'greater_than' | 'less_than' | 'between_two_numbers';
    amountValue1: string;
    amountValue2: string;
}

interface ComponentInvoice {
    id: string;
    amountDue: number;
    amountPaid: number;
    amountRemaining: number;
    currency: string;
    status: string;
    dueDate: string;
    createdDate: string;
    invoice_pdf?: string;
    hosted_invoice_url?: string;
    payment_description?: string;
    payment_name?: string;
    brandName?: string;
    brandId?: string;
}

const InvoicesTab: React.FC = () => {
    const { currentUser } = useUser() as UserExistsState;
    
    const [invoices, setInvoices] = useState<ComponentInvoice[]>([]);
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
    const [invoiceError, setInvoiceError] = useState<string | null>(null);
    const [availableBrands, setAvailableBrands] = useState<Array<{ id: string; name: string }>>([]);
    
    // Initialize filter with default values
    const [filter, setFilter] = useState<InvoiceFilter>({
        brands: [],
        statuses: [],
        dateModifier: 'all_time',
        startDate: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        filterDays: '30',
        amountModifier: 'no_amount_filter',
        amountValue1: '',
        amountValue2: '',
    });

    const loadInvoices = async (appliedFilter?: InvoiceFilter) => {
        if (!currentUser) {
            setIsLoadingInvoices(false);
            return;
        }

        setIsLoadingInvoices(true);
        setInvoiceError(null);

        try {
            // Build API filters from component filter
            const activeFilter = appliedFilter || filter;
            const apiFilters: any = {};

            // Brand filters
            if (activeFilter.brands.length > 0) {
                // For now, we'll just use the first brand. 
                // The API could be enhanced to support multiple brands
                apiFilters.brandId = activeFilter.brands[0];
            }

            // Date filters
            if (activeFilter.dateModifier !== 'all_time') {
                if (activeFilter.dateModifier === 'in_between') {
                    apiFilters.startDate = activeFilter.startDate;
                    apiFilters.endDate = activeFilter.endDate;
                } else if (activeFilter.dateModifier === 'after') {
                    apiFilters.startDate = activeFilter.startDate;
                } else if (activeFilter.dateModifier === 'before') {
                    apiFilters.endDate = activeFilter.startDate;
                } else if (activeFilter.dateModifier === 'last_days') {
                    const days = parseInt(activeFilter.filterDays) || 30;
                    apiFilters.startDate = dayjs().subtract(days, 'days').format('YYYY-MM-DD');
                    apiFilters.endDate = dayjs().format('YYYY-MM-DD');
                }
            }

            const apiInvoices: UserInvoice[] = await getUserInvoices_api(apiFilters);

            // Transform API response to component format
            let formattedInvoices: ComponentInvoice[] = apiInvoices.map(inv => {
                let dueDate = 'N/A';
                if (inv.due_date) {
                    const timestamp = parseInt(inv.due_date, 10);
                    if (!isNaN(timestamp)) {
                        dueDate = new Date(timestamp * 1000).toLocaleDateString();
                    }
                }

                return {
                    id: inv.id,
                    amountDue: inv.amount_due / 100, // Convert from cents
                    amountPaid: inv.amount_paid / 100,
                    amountRemaining: inv.amount_remaining / 100,
                    currency: inv.currency.toUpperCase(),
                    status: inv.status,
                    dueDate,
                    createdDate: new Date(inv.created * 1000).toLocaleDateString(),
                    invoice_pdf: inv.invoice_pdf,
                    hosted_invoice_url: inv.hosted_invoice_url,
                    payment_description: inv.payment_description,
                    payment_name: inv.payment_name,
                    brandName: inv.brand_name,
                    brandId: inv.brand_id,
                };
            });

            // Apply client-side filters that the API might not handle
            // Status filter
            if (activeFilter.statuses.length > 0) {
                formattedInvoices = formattedInvoices.filter(inv => 
                    activeFilter.statuses.includes(inv.status.toLowerCase())
                );
            }

            // Amount filter
            if (activeFilter.amountModifier !== 'no_amount_filter') {
                const amount1 = parseFloat(activeFilter.amountValue1) || 0;
                const amount2 = parseFloat(activeFilter.amountValue2) || 0;

                formattedInvoices = formattedInvoices.filter(inv => {
                    const totalAmount = inv.amountDue;
                    switch (activeFilter.amountModifier) {
                        case 'equal':
                            return totalAmount === amount1;
                        case 'greater_than':
                            return totalAmount > amount1;
                        case 'less_than':
                            return totalAmount < amount1;
                        case 'between_two_numbers':
                            return totalAmount >= amount1 && totalAmount <= amount2;
                        default:
                            return true;
                    }
                });
            }

            // Build available brands list from the invoices
            const brandsMap = new Map<string, string>();
            apiInvoices.forEach(inv => {
                if (inv.brand_id && inv.brand_name) {
                    brandsMap.set(inv.brand_id, inv.brand_name);
                }
            });
            
            setAvailableBrands(Array.from(brandsMap.entries()).map(([id, name]) => ({ id, name })));
            setInvoices(formattedInvoices);
        } catch (error) {
            console.error('Failed to fetch user invoices:', error);
            setInvoiceError('Failed to load invoices. Please try again later.');
        } finally {
            setIsLoadingInvoices(false);
        }
    };

    useEffect(() => {
        loadInvoices();
    }, [currentUser?.userid]);

    const handleFilterChange = (newFilter: InvoiceFilter) => {
        setFilter(newFilter);
    };

    const handleApplyFilters = () => {
        loadInvoices(filter);
    };

    const resetFilters = () => {
        const defaultFilter: InvoiceFilter = {
            brands: [],
            statuses: [],
            dateModifier: 'all_time',
            startDate: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            filterDays: '30',
            amountModifier: 'no_amount_filter',
            amountValue1: '',
            amountValue2: '',
        };
        setFilter(defaultFilter);
        loadInvoices(defaultFilter);
    };

    const hasInvoices = invoices.length > 0;
    const hasFiltersApplied = filter.brands.length > 0 || 
                             filter.statuses.length > 0 || 
                             filter.dateModifier !== 'all_time' || 
                             filter.amountModifier !== 'no_amount_filter';

    const renderInvoiceContent = () => {
        if (isLoadingInvoices) {
            return <StyledLabelP>Loading invoices...</StyledLabelP>;
        }

        if (invoiceError) {
            return <StyledLabelP color="error">{invoiceError}</StyledLabelP>;
        }

        if (!hasInvoices) {
            return (
                <Container className="noInvoicesContainer" padding="48 0" style={{ textAlign: 'center' }}>
                    <StyledLabelP fontSize={18} margin="0 0 8 0">
                        {hasFiltersApplied ? 'No invoices found matching your filters' : 'No invoices found'}
                    </StyledLabelP>
                    <StyledLabelP color="#6B7280">
                        {hasFiltersApplied 
                            ? 'Try adjusting your filter criteria or clearing filters to see all invoices.'
                            : 'Invoices will appear here as they are generated for your brands.'
                        }
                    </StyledLabelP>
                    {hasFiltersApplied && (
                        <Container margin="16 0 0 0">
                            <ButtonSymphony
                                className="clearFiltersButton"
                                onClick={resetFilters}
                                variant="outlined"
                                size="small"
                            >
                                Clear All Filters
                            </ButtonSymphony>
                        </Container>
                    )}
                </Container>
            );
        }

        return <UserInvoiceList invoices={invoices} />;
    };

    return (
        <Container className="invoicesTab">
            <Container
                className="invoicesHeader"
                padding="0 0 24 0"
                borderBottom="1px solid #E5E7EB"
            >
                <StyledLabelP fontSize={20}>All Invoices</StyledLabelP>
                <StyledLabelP fontSize={14} color="#6B7280" margin="4 0 0 0">
                    View and download invoices from all your brands in one place
                </StyledLabelP>
            </Container>

            <Container
                className="invoiceFiltersContainer"
                padding="24 0"
            >
                <InvoiceFilterOptions
                    filter={filter}
                    onFilterChange={handleFilterChange}
                    onApplyFilters={handleApplyFilters}
                    availableBrands={availableBrands}
                    isLoading={isLoadingInvoices}
                />
            </Container>

            <Container
                className="invoiceContentContainer"
                padding="24 0 0 0"
            >
                {renderInvoiceContent()}
            </Container>
        </Container>
    );
};

export default InvoicesTab; 